import { Box, ExpandableSection } from '@amzn/awsui-components-core';
import {
  ContentAccessUser,
  ContentHistory,
  ContentWorkflow,
  Epic,
  Task,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { reorderById } from '../../../common/Arrays';
import CommentThreadsView from '../../../components/comment/CommentThreadsView';
import constants from '../../../common/constants';
import EpicEditorForm from './EpicEditorForm';
import EpicHeader from './EpicHeader';
import TaskEditor from './TaskEditor/TaskEditor';
import { SortableList, SortableRenderArgs } from '../../v2-common/SortableList';

interface Props {
  epics: Epic[];
  isEditable: boolean;
  onUpdated: (epics: any) => void;
  contentId: string;
  workflowId: number;
  showSectionalComments?: boolean;
  workflow?: ContentWorkflow;
  workflowHistory?: ContentHistory[];
  coAuthors?: ContentAccessUser[];
}

interface State {
  editableEpic: Epic;
}

// Stable across renders so SortableList's memoized id list stays valid.
const keyOfEpic = (epic: Epic) => epic.id;

interface EpicItemProps {
  readonly epicItem: Epic;
  readonly setNodeRef: SortableRenderArgs['setNodeRef'];
  readonly style: SortableRenderArgs['style'];
  readonly isEditable: boolean;
  readonly onEpicEditClick: (epic: Epic) => void;
  readonly onEpicChanged: (epic: Epic) => void;
  readonly onEpicTaskChanged: (epicTitle: string, tasks: Task[]) => void;
  readonly onEpicDeleteClick: (epic: Epic) => void;
  readonly epics: Epic[];
  readonly contentId: string;
  readonly workflowId: number;
  readonly showSectionalComments?: boolean;
  readonly workflow?: ContentWorkflow;
  readonly workflowHistory?: ContentHistory[];
  readonly coAuthors?: ContentAccessUser[];
}

// One epic section; the header's drag handle binds to the enclosing SortableList item.
function EpicItem({
  epicItem,
  setNodeRef,
  style,
  isEditable,
  onEpicEditClick,
  onEpicChanged,
  onEpicTaskChanged,
  onEpicDeleteClick,
  epics,
  contentId,
  workflowId,
  showSectionalComments,
  workflow,
  workflowHistory,
  coAuthors,
}: EpicItemProps) {
  const expandableSectionClassName = isEditable
    ? 'sortable-epic'
    : 'sortable-epic sortable-epic-readonly';
  const sectionId = `epics:${epicItem.id}`;
  const epicContent = (
    <>
      <TaskEditor
        epics={epics}
        isEditable={isEditable}
        epicTitle={epicItem.title}
        onChange={onEpicTaskChanged}
        tasks={epicItem.tasks}
      />
      {isEditable ? <hr className="task-creator-divider" /> : null}
      <Box margin={{ left: 's', right: 's' }}>
        <CommentThreadsView
          contentId={contentId}
          workflowId={workflowId}
          sectionId={sectionId}
          workflow={workflow}
          visible={showSectionalComments}
          coAuthors={coAuthors}
          workflowHistory={workflowHistory}
        />
      </Box>
    </>
  );
  return (
    <div ref={setNodeRef} style={style}>
      <Box margin={{ top: 's', bottom: 's' }}>
        <ExpandableSection
          className={expandableSectionClassName}
          defaultExpanded
          variant="container"
          header={
            <EpicHeader
              epic={epicItem}
              isEditable={isEditable}
              onEpicEditClick={onEpicEditClick}
              onEpicChanged={onEpicChanged}
              onEpicDeleteClick={onEpicDeleteClick}
            />
          }
          disableContentPaddings
        >
          {epicContent}
        </ExpandableSection>
      </Box>
    </div>
  );
}

export default class EpicsList extends React.Component<Props, State> {
  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.state = {
      editableEpic: new Epic(),
    };
  }

  public render() {
    const { epics, isEditable } = this.props;
    return <div>{this.renderEpics(epics, isEditable)}</div>;
  }

  private renderEpics = (epics: Epic[], isEditable: boolean) => {
    if (epics.length === 0) {
      return (
        <div>
          {constants.NO_EPICS}
          <br />
        </div>
      );
    }

    const epicEditorForms = epics.map((epic) => this.renderEpicForm(epic));

    return (
      <div>
        <SortableList
          items={epics}
          keyExtractor={keyOfEpic}
          onReorder={this.onReorder}
          isDisabled={!isEditable}
          item={this.renderEpicItem}
        />
        {epicEditorForms}
      </div>
    );
  };

  private renderEpicForm(epic: Epic) {
    const { epics } = this.props;
    return (
      <EpicEditorForm
        key={epic.id}
        epics={epics}
        editEpic={this.onEpicChanged}
        currentEditingEpic={epic}
        onDismissClick={this.onDismissClick}
        isEditable={this.state.editableEpic.id === epic.id}
      />
    );
  }

  private renderEpicItem = (epic: Epic, { setNodeRef, style }: SortableRenderArgs) => {
    const {
      epics,
      isEditable,
      contentId,
      workflow,
      workflowId,
      coAuthors,
      workflowHistory,
      showSectionalComments,
    } = this.props;
    return (
      <EpicItem
        epicItem={epic}
        setNodeRef={setNodeRef}
        style={style}
        isEditable={isEditable}
        onEpicEditClick={this.onEpicEditClick}
        onEpicChanged={this.onEpicChanged}
        onEpicTaskChanged={this.onEpicTaskChanged}
        onEpicDeleteClick={this.onEpicDeleteClick}
        epics={epics}
        contentId={contentId}
        workflowId={workflowId}
        showSectionalComments={showSectionalComments}
        workflow={workflow}
        workflowHistory={workflowHistory}
        coAuthors={coAuthors}
      />
    );
  };

  private onReorder = (orderedIds: readonly string[]) => {
    const { epics, onUpdated } = this.props;
    if (onUpdated) {
      onUpdated(reorderById(epics, orderedIds));
    }
  };

  private onEpicDeleteClick = (item: Epic) => {
    const { epics, onUpdated } = this.props;

    if (onUpdated) {
      onUpdated(
        epics.filter((epic) => {
          return epic.title !== item.title;
        }),
      );
    }
  };

  private onEpicChanged = (editedEpic: Epic) => {
    const { epics, onUpdated } = this.props;
    if (onUpdated) {
      onUpdated(
        epics.map((epic) => {
          return epic.id !== editedEpic.id ? epic : editedEpic;
        }),
      );
    }
  };

  private onEpicEditClick = (epic: Epic) => {
    this.setState({
      editableEpic: epic,
    });
  };

  private onEpicTaskChanged = (epicTitle: string, tasks: Task[]) => {
    const { epics, onUpdated } = this.props;
    const updatedEpics = epics.map((epic) => {
      if (epic.title === epicTitle) {
        return Object.assign(new Epic(), {
          ...epic,
          tasks,
        });
      }

      return epic;
    });

    if (onUpdated) {
      onUpdated(updatedEpics);
    }
  };

  private onDismissClick = () => {
    this.setState({
      editableEpic: new Epic(),
    });
  };
}
